import React, { useState, useEffect } from 'react';
import { apiService } from '../../services/api';
import ImageUpload from './ImageUpload';
import DocumentUpload from './DocumentUpload';

const ApprovalsAffiliationsEdit = () => {
  const [approvalsData, setApprovalsData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [formData, setFormData] = useState({
    bannerTitle: '',
    bannerSubtitle: '',
    bannerImage: '',
    aboutTitle: '',
    aboutContent: '',
    aboutHighlight: '',
    aicteTitle: '',
    aicteDescription: '',
    aicteDocuments: [],
    aktuTitle: '',
    aktuDescription: '',
    aktuDocuments: [],
    bteTitle: '',
    bteDescription: '',
    bteDocuments: [],
    accreditations: []
  });

  useEffect(() => {
    fetchApprovalsData();
  }, []);

  const fetchApprovalsData = async () => {
    try {
      setLoading(true);
      const response = await apiService.getApprovalsAffiliations();
      const data = response.data;
      
      setApprovalsData(data);
      
      // Ensure accreditations have the documentUrl field
      const accreditations = (data.accreditations || []).map(acc => ({
        ...acc,
        documentUrl: acc.documentUrl || '#'
      }));
      
      setFormData({
        bannerTitle: data.bannerTitle || '',
        bannerSubtitle: data.bannerSubtitle || '',
        bannerImage: data.bannerImage || '',
        aboutTitle: data.aboutTitle || '',
        aboutContent: data.aboutContent || '',
        aboutHighlight: data.aboutHighlight || '',
        aicteTitle: data.aicteTitle || '',
        aicteDescription: data.aicteDescription || '',
        aicteDocuments: data.aicteDocuments || [],
        aktuTitle: data.aktuTitle || '',
        aktuDescription: data.aktuDescription || '',
        aktuDocuments: data.aktuDocuments || [],
        bteTitle: data.bteTitle || '',
        bteDescription: data.bteDescription || '',
        bteDocuments: data.bteDocuments || [],
        accreditations: accreditations
      });
    } catch (error) {
      setError('Failed to load approvals affiliations data');
      console.error('Error fetching approvals affiliations:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleDocumentChange = (section, index, field, value) => {
    setFormData(prev => ({
      ...prev,
      [section]: prev[section].map((doc, i) => 
        i === index ? { ...doc, [field]: value } : doc
      )
    }));
  };

  const addDocument = (section) => {
    setFormData(prev => ({
      ...prev,
      [section]: [...prev[section], { year: '', title: '', url: '#' }]
    }));
  };

  const removeDocument = (section, index) => {
    setFormData(prev => ({
      ...prev,
      [section]: prev[section].filter((_, i) => i !== index)
    }));
  };

  const handleAccreditationChange = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      accreditations: prev.accreditations.map((acc, i) => 
        i === index ? { ...acc, [field]: value } : acc
      )
    }));
  };

  const addAccreditation = () => {
    setFormData(prev => ({
      ...prev,
      accreditations: [...prev.accreditations, { name: '', fullName: '', icon: 'fas fa-medal', documentUrl: '#' }]
    }));
  };

  const removeAccreditation = (index) => {
    setFormData(prev => ({
      ...prev,
      accreditations: prev.accreditations.filter((_, i) => i !== index)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    setSuccess('');

    try {
      if (approvalsData?.id) {
        await apiService.updateApprovalsAffiliations(approvalsData.id, formData);
        setSuccess('About page content updated successfully!');
      } else {
        await apiService.createApprovalsAffiliations(formData);
        setSuccess('About page content created successfully!');
      }
      
      // Refresh data
      await fetchApprovalsData();
    } catch (error) {
      setError('Failed to save about page data');
      console.error('Error saving approvals affiliations:', error);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-lg text-slate">
          <i className="fas fa-spinner fa-spin mr-2 text-crimson"></i>
          Loading about page data...
        </div>
      </div>
    );
  }

  return (
    <div className="font-sans">
      <div className="mb-8">
        <h1 className="text-3xl font-sans font-bold text-slate mb-2">
          About Page Management
        </h1>
        <p className="text-gray-600">
          Manage the content for the About page including banner, approvals, affiliations, and accreditations.
        </p>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-6">
          <i className="fas fa-exclamation-circle mr-2"></i>
          {error}
        </div>
      )}

      {success && (
        <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg mb-6">
          <i className="fas fa-check-circle mr-2"></i>
          {success}
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-8">
        {/* Banner Section */}
        <div className="bg-white rounded-lg border border-border_gray shadow-sm p-6">
          <h2 className="text-xl font-sans font-semibold text-slate mb-4">Banner Section</h2>
          
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-slate mb-2">Banner Title</label>
              <input
                type="text"
                value={formData.bannerTitle}
                onChange={(e) => handleInputChange('bannerTitle', e.target.value)}
                className="w-full px-4 py-3 border border-border_gray rounded-lg focus:outline-none focus:ring-2 focus:ring-crimson"
                placeholder="About FiT Engineering College"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate mb-2">Banner Subtitle</label>
              <textarea
                value={formData.bannerSubtitle}
                onChange={(e) => handleInputChange('bannerSubtitle', e.target.value)}
                className="w-full px-4 py-3 border border-border_gray rounded-lg focus:outline-none focus:ring-2 focus:ring-crimson"
                rows={2}
                placeholder="Excellence in Engineering Education | Building Tomorrow's Technology Leaders"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate mb-2">Banner Image</label>
              <ImageUpload
                value={formData.bannerImage}
                onChange={(value) => handleInputChange('bannerImage', value)}
                placeholder="Banner background image filename"
              />
            </div>
          </div>
        </div>

        {/* About Section */}
        <div className="bg-white rounded-lg border border-border_gray shadow-sm p-6">
          <h2 className="text-xl font-sans font-semibold text-slate mb-4">About Section</h2>
          
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-slate mb-2">About Title</label>
              <input
                type="text"
                value={formData.aboutTitle}
                onChange={(e) => handleInputChange('aboutTitle', e.target.value)}
                className="w-full px-4 py-3 border border-border_gray rounded-lg focus:outline-none focus:ring-2 focus:ring-crimson"
                placeholder="About FiT Engineering College"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate mb-2">About Content</label>
              <textarea
                value={formData.aboutContent}
                onChange={(e) => handleInputChange('aboutContent', e.target.value)}
                className="w-full px-4 py-3 border border-border_gray rounded-lg focus:outline-none focus:ring-2 focus:ring-crimson"
                rows={4}
                placeholder="Description about the college..."
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate mb-2">Highlight Box Content</label>
              <input
                type="text"
                value={formData.aboutHighlight}
                onChange={(e) => handleInputChange('aboutHighlight', e.target.value)}
                className="w-full px-4 py-3 border border-border_gray rounded-lg focus:outline-none focus:ring-2 focus:ring-crimson"
                placeholder="AKTU Affiliated & BTE UP Approved - Recognized for academic excellence."
              />
            </div>
          </div>
        </div>

        {/* AICTE Section */}
        <div className="bg-white rounded-lg border border-border_gray shadow-sm p-6">
          <h2 className="text-xl font-sans font-semibold text-slate mb-4">AICTE Section</h2>
          
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate mb-2">AICTE Title</label>
                <input
                  type="text"
                  value={formData.aicteTitle}
                  onChange={(e) => handleInputChange('aicteTitle', e.target.value)}
                  className="w-full px-4 py-3 border border-border_gray rounded-lg focus:outline-none focus:ring-2 focus:ring-crimson"
                  placeholder="AICTE"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate mb-2">AICTE Description</label>
                <input
                  type="text"
                  value={formData.aicteDescription}
                  onChange={(e) => handleInputChange('aicteDescription', e.target.value)}
                  className="w-full px-4 py-3 border border-border_gray rounded-lg focus:outline-none focus:ring-2 focus:ring-crimson"
                  placeholder="All India Council for Technical Education"
                />
              </div>
            </div>

            <div>
              <div className="flex justify-between items-center mb-4">
                <label className="block text-sm font-medium text-slate">AICTE Documents</label>
                <button
                  type="button"
                  onClick={() => addDocument('aicteDocuments')}
                  className="px-4 py-2 bg-crimson text-white rounded-lg hover:bg-crimson/90 flex items-center gap-2 text-sm"
                >
                  <i className="fas fa-plus"></i>
                  Add Document
                </button>
              </div>
              
              <div className="space-y-3">
                {formData.aicteDocuments.map((doc, index) => (
                  <div key={index} className="border border-gray-200 rounded-lg p-4 relative">
                    <button
                      type="button"
                      onClick={() => removeDocument('aicteDocuments', index)}
                      className="absolute top-2 right-2 text-red-500 hover:text-red-700"
                    >
                      <i className="fas fa-times-circle"></i>
                    </button>
                    <div className="space-y-3">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <input
                          type="text"
                          value={doc.year}
                          onChange={(e) => handleDocumentChange('aicteDocuments', index, 'year', e.target.value)}
                          className="px-3 py-2 border border-border_gray rounded text-sm"
                          placeholder="2024-25"
                        />
                        <input
                          type="text"
                          value={doc.title}
                          onChange={(e) => handleDocumentChange('aicteDocuments', index, 'title', e.target.value)}
                          className="px-3 py-2 border border-border_gray rounded text-sm"
                          placeholder="Document Title"
                        />
                      </div>
                      <DocumentUpload
                        value={doc.url}
                        onChange={(value) => handleDocumentChange('aicteDocuments', index, 'url', value)}
                        placeholder="Upload or enter document URL"
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* AKTU Section */}
        <div className="bg-white rounded-lg border border-border_gray shadow-sm p-6">
          <h2 className="text-xl font-sans font-semibold text-slate mb-4">AKTU Section</h2>
          
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate mb-2">AKTU Title</label>
                <input
                  type="text"
                  value={formData.aktuTitle}
                  onChange={(e) => handleInputChange('aktuTitle', e.target.value)}
                  className="w-full px-4 py-3 border border-border_gray rounded-lg focus:outline-none focus:ring-2 focus:ring-crimson"
                  placeholder="AKTU"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate mb-2">AKTU Description</label>
                <input
                  type="text"
                  value={formData.aktuDescription}
                  onChange={(e) => handleInputChange('aktuDescription', e.target.value)}
                  className="w-full px-4 py-3 border border-border_gray rounded-lg focus:outline-none focus:ring-2 focus:ring-crimson"
                  placeholder="Dr. A.P.J. Abdul Kalam Technical University"
                />
              </div>
            </div>

            <div>
              <div className="flex justify-between items-center mb-4">
                <label className="block text-sm font-medium text-slate">AKTU Documents</label>
                <button
                  type="button"
                  onClick={() => addDocument('aktuDocuments')}
                  className="px-4 py-2 bg-crimson text-white rounded-lg hover:bg-crimson/90 flex items-center gap-2 text-sm"
                >
                  <i className="fas fa-plus"></i>
                  Add Document
                </button>
              </div>
              
              <div className="space-y-3">
                {formData.aktuDocuments.map((doc, index) => (
                  <div key={index} className="border border-gray-200 rounded-lg p-4 relative">
                    <button
                      type="button"
                      onClick={() => removeDocument('aktuDocuments', index)}
                      className="absolute top-2 right-2 text-red-500 hover:text-red-700"
                    >
                      <i className="fas fa-times-circle"></i>
                    </button>
                    <div className="space-y-3">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <input
                          type="text"
                          value={doc.year}
                          onChange={(e) => handleDocumentChange('aktuDocuments', index, 'year', e.target.value)}
                          className="px-3 py-2 border border-border_gray rounded text-sm"
                          placeholder="2024-25"
                        />
                        <input
                          type="text"
                          value={doc.title}
                          onChange={(e) => handleDocumentChange('aktuDocuments', index, 'title', e.target.value)}
                          className="px-3 py-2 border border-border_gray rounded text-sm"
                          placeholder="Document Title"
                        />
                      </div>
                      <DocumentUpload
                        value={doc.url}
                        onChange={(value) => handleDocumentChange('aktuDocuments', index, 'url', value)}
                        placeholder="Upload or enter document URL"
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* BTE UP Section */}
        <div className="bg-white rounded-lg border border-border_gray shadow-sm p-6">
          <h2 className="text-xl font-sans font-semibold text-slate mb-4">BTE UP Section</h2>
          
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate mb-2">BTE Title</label>
                <input
                  type="text"
                  value={formData.bteTitle}
                  onChange={(e) => handleInputChange('bteTitle', e.target.value)}
                  className="w-full px-4 py-3 border border-border_gray rounded-lg focus:outline-none focus:ring-2 focus:ring-crimson"
                  placeholder="BTE UP"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate mb-2">BTE Description</label>
                <input
                  type="text"
                  value={formData.bteDescription}
                  onChange={(e) => handleInputChange('bteDescription', e.target.value)}
                  className="w-full px-4 py-3 border border-border_gray rounded-lg focus:outline-none focus:ring-2 focus:ring-crimson"
                  placeholder="Board of Technical Education, Uttar Pradesh"
                />
              </div>
            </div>

            <div>
              <div className="flex justify-between items-center mb-4">
                <label className="block text-sm font-medium text-slate">BTE Documents</label>
                <button
                  type="button"
                  onClick={() => addDocument('bteDocuments')}
                  className="px-4 py-2 bg-crimson text-white rounded-lg hover:bg-crimson/90 flex items-center gap-2 text-sm"
                >
                  <i className="fas fa-plus"></i>
                  Add Document
                </button>
              </div>
              
              <div className="space-y-3">
                {formData.bteDocuments.map((doc, index) => (
                  <div key={index} className="border border-gray-200 rounded-lg p-4 relative">
                    <button
                      type="button"
                      onClick={() => removeDocument('bteDocuments', index)}
                      className="absolute top-2 right-2 text-red-500 hover:text-red-700"
                    >
                      <i className="fas fa-times-circle"></i>
                    </button>
                    <div className="space-y-3">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <input
                          type="text"
                          value={doc.year}
                          onChange={(e) => handleDocumentChange('bteDocuments', index, 'year', e.target.value)}
                          className="px-3 py-2 border border-border_gray rounded text-sm"
                          placeholder="2024-25"
                        />
                        <input
                          type="text"
                          value={doc.title}
                          onChange={(e) => handleDocumentChange('bteDocuments', index, 'title', e.target.value)}
                          className="px-3 py-2 border border-border_gray rounded text-sm"
                          placeholder="Document Title"
                        />
                      </div>
                      <DocumentUpload
                        value={doc.url}
                        onChange={(value) => handleDocumentChange('bteDocuments', index, 'url', value)}
                        placeholder="Upload or enter document URL"
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Accreditations Section */}
        <div className="bg-white rounded-lg border border-border_gray shadow-sm p-6">
          <h2 className="text-xl font-sans font-semibold text-slate mb-4">Accreditations</h2>
          
          <div>
            <div className="flex justify-between items-center mb-4">
              <label className="block text-sm font-medium text-slate">Accreditation Organizations</label>
              <button
                type="button"
                onClick={addAccreditation}
                className="px-4 py-2 bg-crimson text-white rounded-lg hover:bg-crimson/90 flex items-center gap-2 text-sm"
              >
                <i className="fas fa-plus"></i>
                Add Accreditation
              </button>
            </div>
            
            <div className="space-y-3">
              {formData.accreditations.map((acc, index) => (
                <div key={index} className="border border-gray-200 rounded-lg p-4 relative space-y-4">
                  <button
                    type="button"
                    onClick={() => removeAccreditation(index)}
                    className="absolute top-2 right-2 text-red-500 hover:text-red-700"
                  >
                    <i className="fas fa-times-circle"></i>
                  </button>
                  
                  <div className="space-y-3">
                    {/* Basic Accreditation Info */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                      <input
                        type="text"
                        value={acc.name}
                        onChange={(e) => handleAccreditationChange(index, 'name', e.target.value)}
                        className="px-3 py-2 border border-border_gray rounded text-sm"
                        placeholder="NAAC"
                      />
                      <input
                        type="text"
                        value={acc.fullName}
                        onChange={(e) => handleAccreditationChange(index, 'fullName', e.target.value)}
                        className="px-3 py-2 border border-border_gray rounded text-sm"
                        placeholder="Full Name"
                      />
                      <input
                        type="text"
                        value={acc.icon}
                        onChange={(e) => handleAccreditationChange(index, 'icon', e.target.value)}
                        className="px-3 py-2 border border-border_gray rounded text-sm"
                        placeholder="fas fa-medal"
                      />
                    </div>

                    {/* Document Attachment */}
                    <div>
                      <label className="block text-sm font-medium text-slate mb-2">
                        {acc.name || 'Accreditation'} Document
                      </label>
                      <DocumentUpload
                        value={acc.documentUrl || '#'}
                        onChange={(value) => handleAccreditationChange(index, 'documentUrl', value)}
                        placeholder="Upload accreditation certificate/document"
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Submit Button */}
        <div className="flex justify-end">
          <button
            type="submit"
            disabled={saving}
            className="px-6 py-3 bg-crimson text-white rounded-lg font-medium hover:bg-crimson/90 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            {saving ? (
              <>
                <i className="fas fa-spinner fa-spin"></i>
                Saving...
              </>
            ) : (
              <>
                <i className="fas fa-save"></i>
                Save About Page Content
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default ApprovalsAffiliationsEdit;